import { useState } from 'react';
import { useGameContext } from '@/state/GameContext';
import { textureCatalog } from '@/engine/TextureCatalog';
import type { ItemCategory } from '@/types/domain';

const CATS: ItemCategory[] = ['tool', 'weapon', 'armor', 'block', 'material', 'food', 'transport', 'redstone', 'machine', 'container', 'utility', 'decorative', 'misc', 'magic', 'structure'];

export function DevPanel() {
  const game = useGameContext();
  const [textureQuery, setTextureQuery] = useState('');
  const [forceTex, setForceTex] = useState('');
  const [forceCat, setForceCat] = useState('');

  const results = textureQuery ? textureCatalog.search(textureQuery, 30) : [];
  const dev = game.devInfo;
  const norm = dev.lastNorm;

  return (
    <div className="dev">
      <h4>Developer panel</h4>
      <div className="row">
        <button className={`btn ghost${game.settings.mockAi ? ' primary' : ''}`} onClick={() => game.updateSettings({ mockAi: !game.settings.mockAi })}>
          Mock AI: {game.settings.mockAi ? 'ON' : 'off'}
        </button>
        <button className={`btn ghost${game.settings.devMode ? '' : ''}`} onClick={() => game.updateSettings({ strictness: (game.settings.strictness + 1) % 3 })}>
          Strictness: {['lenient', 'moderate', 'strict'][game.settings.strictness]}
        </button>
        <button className="btn" onClick={() => game.updateSettings({ interpretation: game.settings.interpretation === 'shaped' ? 'shapeless' : game.settings.interpretation === 'shapeless' ? 'auto' : 'shaped' })}>
          Interpretation: {game.settings.interpretation}
        </button>
      </div>

      <div className="row">
        <label className="kv">Model</label>
        <input type="text" value={game.settings.model} onChange={(e) => game.updateSettings({ model: e.target.value })} style={{ flex: 1 }} />
      </div>

      <div className="row">
        <label className="kv">Force texture</label>
        <input type="text" placeholder="minecraft:..." value={forceTex} onChange={(e) => setForceTex(e.target.value)} />
        <button className="btn" onClick={() => game.setRerollLock(forceTex || undefined, game.rerollLockCategory)}>Apply</button>
        <span className="muted">active: {game.rerollLockTexture || 'none'}</span>
      </div>
      <div className="row">
        <label className="kv">Force category</label>
        <select value={forceCat} onChange={(e) => setForceCat(e.target.value)}>
          <option value="">none</option>
          {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button className="btn" onClick={() => game.setRerollLock(game.rerollLockTexture, (forceCat || undefined) as ItemCategory | undefined)}>Apply</button>
      </div>

      <div className="row">
        <button className="btn" onClick={() => game.clearCache()}>Clear recipe cache</button>
        <button className="btn" onClick={() => { game.clearGrid(); game.placeItem(4, 'minecraft:raw_iron'); game.placeItem(5, 'minecraft:coal'); }}>Test invalid (raw iron + coal)</button>
        <button className="btn" onClick={() => { game.clearGrid(); void game.craft(); }}>Clear & craft</button>
      </div>

      <h4>Normalized recipe key</h4>
      {norm ? (
        <pre>
{JSON.stringify(
  {
    shapedKey: norm.shapedKey,
    shapelessKey: norm.shapelessKey,
    boundingBox: norm.boundingBox,
    ingredients: norm.ingredientIds,
    cacheKey: dev.lastKey,
  },
  null,
  2,
)}
        </pre>
      ) : (
        <div className="muted">Craft something to inspect the normalized key.</div>
      )}

      <h4>Last AI request ({dev.lastRequest ? `${dev.lastRequest.textureCandidates.length} texture candidates` : 'none'})</h4>
      <pre>{dev.lastRequest ? JSON.stringify({ ...dev.lastRequest, textureCandidates: dev.lastRequest.textureCandidates.length }, null, 2) : 'none'}</pre>

      <h4>Last validated AI response</h4>
      <pre>{dev.lastResponse ? JSON.stringify(dev.lastResponse, null, 2) : 'none'}</pre>

      <h4>Texture catalog search ({textureCatalog.count()} entries)</h4>
      <input className="search" style={{ marginBottom: 6 }} placeholder="search textures..." value={textureQuery} onChange={(e) => setTextureQuery(e.target.value)} />
      <div className="muted" style={{ maxHeight: 120, overflow: 'auto' }}>
        {results.map((r) => (
          <div key={r.id}>{r.id} :: {r.name} :: [{r.tags.slice(0, 5).join(', ')}]</div>
        ))}
      </div>

      <h4>Export debugging data</h4>
      <button className="btn" onClick={async () => { const j = await game.exportSave(); const blob = new Blob([j], { type: 'application/json' }); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'debug-save.json'; a.click(); }}>Download save JSON</button>
    </div>
  );
}
